import { Link } from "react-router-dom";
import { AlertTriangle, TrendingUp } from "lucide-react";

const CARD = "rounded-lg border border-slate-200 bg-white p-4 shadow-sm";

const Figure = ({ label, value, tone = "blue", testid }) => (
  <div className="rounded-md bg-slate-50 p-3" data-testid={testid}>
    <p className={`text-3xl font-semibold ${tone === "red" ? "text-red-900"
      : tone === "amber" ? "text-amber-900" : "text-[var(--hcd-blue)]"}`}>{value}</p>
    <p className="text-xs text-slate-600">{label}</p>
  </div>
);

export const ExecutiveView = ({ data }) => (
  <div className="space-y-6" data-testid="executive-view">
    <section className={CARD} aria-labelledby="exec-sla">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="exec-sla" className="text-base font-semibold md:text-lg">
          Enterprise service levels
        </h2>
        <Link to={data.sla.link} className="text-sm text-[var(--hcd-blue)] underline"
              data-testid="exec-sla-link">Open the report</Link>
      </div>
      {data.sla.available ? (
        <>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Figure label="SLA attainment" testid="exec-attainment"
                    value={data.sla.attainment_pct === null ? "—"
                      : `${data.sla.attainment_pct}%`} />
            <Figure label="Requests measured" value={data.sla.measured}
                    testid="exec-measured" />
            <Figure label="Open work" value={data.totals.open_work} testid="exec-open" />
            <Figure label="Breached now" value={data.totals.breached} tone="red"
                    testid="exec-breached" />
          </div>
          {data.sla.worst_sites.length > 0 && (
            <ul className="mt-3 text-sm" data-testid="exec-worst-sites">
              {data.sla.worst_sites.map((row) => (
                <li key={row.location_name} className="flex justify-between border-b
                    border-slate-100 py-1">
                  <span>{row.location_name}</span>
                  <span className="text-slate-600">
                    {row.attainment_pct}% of {row.measured} measured
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-slate-600" data-testid="exec-sla-method">
            {data.sla.method}
          </p>
        </>
      ) : (
        <p className="mt-2 text-sm text-slate-600" data-testid="exec-sla-unavailable">
          {data.sla.why || "Service levels are not available to you."}
        </p>
      )}
    </section>

    <section className={CARD} aria-labelledby="exec-providers">
      <h2 id="exec-providers" className="text-base font-semibold md:text-lg">
        Open work by team
      </h2>
      <div className="mt-3 overflow-x-auto" tabIndex={0} role="region"
           aria-label="Open work, unassigned, at risk and breached by provider">
        <table className="w-full text-sm" data-testid="exec-provider-table">
          <thead className="text-left text-xs uppercase text-slate-600">
            <tr>
              <th className="py-1">Provider</th><th className="py-1">Open</th>
              <th className="py-1">Unassigned</th><th className="py-1">At risk</th>
              <th className="py-1">Breached</th><th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {data.providers.map((row) => (
              <tr key={row.provider_key} className="border-t border-slate-100"
                  data-testid={`exec-provider-${row.provider_key}`}>
                <td className="py-2 font-medium">{row.provider_name}</td>
                <td className="py-2">{row.open_total}</td>
                <td className="py-2">{row.unassigned}</td>
                <td className="py-2 text-amber-900">{row.sla_at_risk}</td>
                <td className="py-2 text-red-900">{row.breached}</td>
                <td className="py-2 text-right">
                  <Link to={row.link} className="text-[var(--hcd-blue)] underline"
                        data-testid={`exec-provider-link-${row.provider_key}`}>Queue</Link>
                </td>
              </tr>
            ))}
            {data.providers.length === 0 && (
              <tr><td colSpan={6} className="py-2 text-slate-600"
                      data-testid="exec-providers-empty">
                No provider has published a service yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-slate-600" data-testid="exec-provider-method">
        Open and unassigned are full counts. At risk and breached are measured one request at a
        time against each request's own pinned SLA policy, on the oldest 500 open requests per
        team.
      </p>
    </section>

    <div className="grid gap-6 lg:grid-cols-2">
      <section className={CARD} aria-labelledby="exec-week">
        <div className="flex items-center justify-between">
          <h2 id="exec-week" className="text-base font-semibold md:text-lg">
            Outages and the week ahead
          </h2>
          <Link to={data.outages.link} className="text-sm text-[var(--hcd-blue)] underline"
                data-testid="exec-outages-link">Service status</Link>
        </div>
        <p className="mt-2 flex items-center gap-2 text-sm" data-testid="exec-outage-count">
          <AlertTriangle className="h-4 w-4 text-red-800" aria-hidden="true" />
          {data.outages.active} active outage(s)
          {data.outages.at_my_site > 0 && `, ${data.outages.at_my_site} at your site`}
        </p>
        <ul className="mt-2 space-y-1 text-sm" data-testid="exec-week-ahead">
          {data.week_ahead.map((row, i) => (
            <li key={`${row.title}-${i}`} className="flex justify-between border-b
                border-slate-100 py-1">
              <span>
                <span className="mr-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase">
                  {row.kind}
                </span>
                {row.title}
              </span>
              <span className="text-xs text-slate-600">
                {String(row.start).slice(0, 10)}
              </span>
            </li>
          ))}
          {data.week_ahead.length === 0 && (
            <li className="text-slate-600" data-testid="exec-week-empty">
              Nothing is scheduled in the next seven days.
            </li>
          )}
        </ul>
      </section>

      <section className={CARD} aria-labelledby="exec-renewals">
        <div className="flex items-center justify-between">
          <h2 id="exec-renewals" className="text-base font-semibold md:text-lg">
            Contract renewals due
          </h2>
          <Link to={data.renewals.link} className="text-sm text-[var(--hcd-blue)] underline"
                data-testid="exec-renewals-link">Contracts</Link>
        </div>
        {data.renewals.available ? (
          <>
            <p className="mt-2 text-sm" data-testid="exec-renewals-count">
              {data.renewals.count} date(s) inside the next two quarters
            </p>
            <ul className="mt-2 space-y-1 text-sm" data-testid="exec-renewal-rows">
              {data.renewals.rows.map((row) => (
                <li key={`${row.contract_id}-${row.kind}`} className="flex justify-between
                    border-b border-slate-100 py-1">
                  <span>{row.vendor_name} — {row.title}</span>
                  <span className="text-xs text-slate-600">
                    {row.kind === "notice_deadline" ? "notice by " : "ends "}{row.date}
                  </span>
                </li>
              ))}
              {data.renewals.rows.length === 0 && (
                <li className="text-slate-600" data-testid="exec-renewals-empty">
                  Nothing falls due in the next two quarters.
                </li>
              )}
            </ul>
          </>
        ) : (
          <p className="mt-2 text-sm text-slate-600" data-testid="exec-renewals-unavailable">
            {data.renewals.why}
          </p>
        )}
      </section>
    </div>

    <section className={CARD} aria-labelledby="exec-releases">
      <div className="flex items-center justify-between">
        <h2 id="exec-releases" className="flex items-center gap-2 text-base font-semibold
            md:text-lg">
          <TrendingUp className="h-4 w-4" aria-hidden="true" />
          Release performance
        </h2>
        <Link to={data.releases.link} className="text-sm text-[var(--hcd-blue)] underline"
              data-testid="exec-releases-link">Releases</Link>
      </div>
      {data.releases.available ? (
        <>
          <ul className="mt-2 space-y-1 text-sm" data-testid="exec-release-rows">
            {data.releases.rows.map((row) => (
              <li key={row.service_key || row.service_name} className="flex justify-between
                  border-b border-slate-100 py-1">
                <span>{row.service_name}</span>
                <span className="text-xs text-slate-600">
                  {row.changes} change(s) · {row.change_failure_rate_pct ?? "—"}% failure
                  {row.median_lead_time_hours !== null
                    && row.median_lead_time_hours !== undefined
                    && ` · ${row.median_lead_time_hours}h median lead time`}
                </span>
              </li>
            ))}
            {data.releases.rows.length === 0 && (
              <li className="text-slate-600" data-testid="exec-releases-empty">
                No change has been recorded in the last ninety days.
              </li>
            )}
          </ul>
          <p className="mt-2 text-xs text-slate-600" data-testid="exec-releases-method">
            {data.releases.method}
          </p>
        </>
      ) : (
        <p className="mt-2 text-sm text-slate-600" data-testid="exec-releases-unavailable">
          {data.releases.why}
        </p>
      )}
    </section>

    <p className="text-xs text-slate-600" data-testid="exec-method">{data.method}</p>
  </div>
);
